import Button from "app/components/Button";
import CautionDangerIcon from "app/components/Icons/CautionDangerIcon";
import React, { useState, useRef, useEffect } from "react";

type OTPProps = {
  onSubmit: (code: string) => void;
  error?: string;
  disabled?: boolean;
  isLoading?: boolean;
  length?: number;
  label?: string;
};

export default function OTPFeature({
  onSubmit,
  error,
  disabled,
  isLoading,
  length = 6, // default to 6 if not provided
  label,
}: OTPProps) {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(""));
  const inputRefs = useRef<HTMLInputElement[]>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index: number, value: string) => {
    if (isNaN(Number(value))) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value !== "" && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && index > 0 && otp[index] === "") {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = () => {
    const code = otp.join("");
    if (code.length === length) {
      onSubmit(code);
    }
  };

  //Handle paste event (support dynamic length)
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");
    if (!/^\d+$/.test(pastedData)) return;

    const pastedDigits = pastedData.split("").slice(0, length);
    setOtp(pastedDigits.concat(Array(length - pastedDigits.length).fill("")));

    const nextIndex =
      pastedDigits.length < length ? pastedDigits.length : length - 1;
    inputRefs.current[nextIndex]?.focus();
  };

  const isTyping = otp.some((d) => d !== "") && otp.some((d) => d === "");
  const currentError =
    error || (isTyping ? `Must enter ${length} numbers` : undefined);

  return (
    <div className="flex flex-col items-center gap-[24px] w-full">
      <div className="flex flex-col items-center gap-[12px] w-full">
        {label && (
          <span
            className={`text-[14px] leading-[21px] font-normal ${currentError ? "text-danger" : "text-secondary"}`}
          >
            {label}
          </span>
        )}
        <div className="flex flex-col gap-[8px] items-start">
          <div className="flex flex-row items-center gap-[6px]">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) =>
                  (inputRefs.current[index] = el as HTMLInputElement)
                }
                className={`w-[85.5px] h-[90px] border-[1px] rounded-[4px] focus:outline-none pt-[15px] pb-[15px] text-black font-light text-[52px] leading-[140%] tracking-normal align-middle text-center transition-all ${currentError ? "border-danger" : "border-gray-100 focus:border-secondary"}`}
                type="text"
                maxLength={1}
                value={digit}
                disabled={disabled || isLoading}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
              />
            ))}
          </div>
          {currentError && (
            <div className="flex items-center justify-start gap-[6px]">
              <CautionDangerIcon size={14} color="#EB5757" />
              <p className="text-[10px] leading-[15px] text-danger font-normal">
                {currentError}
              </p>
            </div>
          )}
        </div>
      </div>
      <Button
        type="button"
        className="w-full h-[56px]"
        onClick={handleSubmit}
        disabled={otp.some((digit) => digit === "") || disabled}
      >
        Verify
      </Button>
    </div>
  );
}
